import { supabase, isConfigured } from './supabase';

const TWILIO_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twilio`;

export interface Message {
  id: string;
  direction: 'inbound' | 'outbound';
  channel: 'whatsapp' | 'sms';
  body: string;
  status: string | null;
  sent_at: string;
}

export interface Call {
  id: string;
  direction: 'inbound' | 'outbound';
  status: string;
  started_at: string;
  duration_seconds: number | null;
  recording_url: string | null;
}

export interface ContactComms {
  whatsapp_number: string | null;
  sms_number: string | null;
}

export interface InboxItem {
  contact_id: string;
  contact_name: string;
  company: string | null;
  channel: 'whatsapp' | 'sms';
  last_message: string;
  last_message_at: string;
  direction: 'inbound' | 'outbound';
  unread: boolean;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
  };
}

export async function getContactComms(contactId: string): Promise<ContactComms> {
  if (!isConfigured) return { whatsapp_number: null, sms_number: null };

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${TWILIO_FUNCTION_URL}/comms?contact_id=${contactId}`, { headers });

    if (!response.ok) {
      console.error('[twilio] Get comms failed:', response.status);
      return { whatsapp_number: null, sms_number: null };
    }

    return await response.json();
  } catch (error) {
    console.error('[twilio] Get comms failed:', error);
    return { whatsapp_number: null, sms_number: null };
  }
}

export async function saveContactComms(
  orgId: string,
  contactId: string,
  whatsappNumber: string | null,
  smsNumber: string | null
): Promise<boolean> {
  if (!isConfigured) return false;

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${TWILIO_FUNCTION_URL}/comms`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        org_id: orgId,
        contact_id: contactId,
        whatsapp_number: whatsappNumber,
        sms_number: smsNumber,
      }),
    });

    return response.ok;
  } catch (error) {
    console.error('[twilio] Save comms failed:', error);
    return false;
  }
}

export async function sendMessage(
  orgId: string,
  contactId: string,
  channel: 'whatsapp' | 'sms',
  body: string,
  createFollowup = true
): Promise<{ success: boolean; message_sid?: string; error?: string }> {
  if (!isConfigured) return { success: false, error: 'Not configured' };

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${TWILIO_FUNCTION_URL}/message/send`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        org_id: orgId,
        contact_id: contactId,
        channel,
        body,
        create_followup: createFollowup,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Send failed' };
    }

    return data;
  } catch (error) {
    console.error('[twilio] Send message failed:', error);
    return { success: false, error: (error as Error).message };
  }
}

export async function getContactMessages(contactId: string, limit = 100): Promise<Message[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_contact_messages', {
    p_contact_id: contactId,
    p_limit: limit,
  });

  if (error) {
    console.error('[twilio] Get messages failed:', error);
    return [];
  }

  return data as Message[];
}

export async function getContactCalls(contactId: string, limit = 50): Promise<Call[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_contact_calls', {
    p_contact_id: contactId,
    p_limit: limit,
  });

  if (error) {
    console.error('[twilio] Get calls failed:', error);
    return [];
  }

  return data as Call[];
}

export async function getInbox(orgId: string, limit = 50): Promise<InboxItem[]> {
  if (!isConfigured) return [];

  const { data, error } = await supabase.rpc('get_sales_inbox', {
    p_org_id: orgId,
    p_limit: limit,
  });

  if (error) {
    console.error('[twilio] Get inbox failed:', error);
    return [];
  }

  return data as InboxItem[];
}

export async function getVoiceToken(): Promise<{ token: string; identity: string } | null> {
  if (!isConfigured) return null;

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${TWILIO_FUNCTION_URL}/voice/token`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      console.error('[twilio] Get voice token failed:', response.status);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('[twilio] Get voice token failed:', error);
    return null;
  }
}

export async function ensureConversation(
  orgId: string,
  contactId: string
): Promise<{ success: boolean; conversation_sid?: string; error?: string }> {
  if (!isConfigured) return { success: false, error: 'Not configured' };

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${TWILIO_FUNCTION_URL}/conversation/upsert`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        org_id: orgId,
        contact_id: contactId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Failed' };
    }

    return data;
  } catch (error) {
    console.error('[twilio] Ensure conversation failed:', error);
    return { success: false, error: (error as Error).message };
  }
}

export const MESSAGE_TEMPLATES = [
  {
    name: 'Introduction',
    body: `Hi {{name}}, this is {{sender}} from ReHub Studio. I noticed you recently signed up - I'd love to help you get started with our platform. Is now a good time for a quick chat?`,
  },
  {
    name: 'Follow-up',
    body: `Hi {{name}}, just following up on my previous message. I wanted to make sure you have everything you need to get started with ReHub. Let me know if you have any questions!`,
  },
  {
    name: 'Demo reminder',
    body: `Hi {{name}}, just a quick reminder about our demo call scheduled for tomorrow. Looking forward to showing you what ReHub can do for your business!`,
  },
];

export function applyTemplate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] || `{{${key}}}`);
}

export interface UserPhoneNumber {
  phone_number: string;
  friendly_name: string | null;
  is_default: boolean;
}

export async function getUserPhoneNumbers(): Promise<UserPhoneNumber[]> {
  if (!isConfigured) return [];

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('sales_user_phone_numbers')
      .select('phone_number, friendly_name, is_default')
      .eq('user_id', user.id)
      .in('channel', ['voice', 'all'])
      .order('is_default', { ascending: false });

    if (error) {
      console.error('[twilio] Get user phone numbers failed:', error);
      return [];
    }

    return (data as UserPhoneNumber[]) || [];
  } catch (error) {
    console.error('[twilio] Get user phone numbers failed:', error);
    return [];
  }
}

export interface CallerInfo {
  contact_id: string;
  name: string;
  company: string | null;
  phone: string;
}

export async function lookupCallerByPhone(phone: string): Promise<CallerInfo | null> {
  if (!isConfigured || !phone) return null;

  try {
    // Normalize phone number - remove all non-digit except +
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    // Also try without the + prefix
    const phoneWithoutPlus = cleanPhone.replace(/^\+/, '');
    // Last 10 digits for partial matching
    const lastDigits = phoneWithoutPlus.slice(-10);
    
    console.log('[twilio] Looking up caller:', { cleanPhone, phoneWithoutPlus, lastDigits });

    // Search contacts by phone field first (most common)
    const { data: contactData } = await supabase
      .from('sales_contacts')
      .select('id, first_name, last_name, company, phone')
      .or(`phone.ilike.%${lastDigits}%,phone.ilike.%${cleanPhone}%,phone.ilike.%${phoneWithoutPlus}%`)
      .limit(1)
      .single();

    if (contactData) {
      console.log('[twilio] Found contact by phone:', contactData);
      return {
        contact_id: contactData.id,
        name: `${contactData.first_name || ''} ${contactData.last_name || ''}`.trim() || 'Unknown',
        company: contactData.company,
        phone: contactData.phone || cleanPhone,
      };
    }

    // Fall back to sales_contact_comms
    const { data: commsData } = await supabase
      .from('sales_contact_comms')
      .select('contact_id, sales_contacts!inner(id, first_name, last_name, company)')
      .or(`whatsapp_number.ilike.%${lastDigits}%,sms_number.ilike.%${lastDigits}%`)
      .limit(1)
      .single();

    if (commsData?.sales_contacts) {
      const contact = commsData.sales_contacts as any;
      console.log('[twilio] Found contact by comms:', contact);
      return {
        contact_id: contact.id,
        name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown',
        company: contact.company,
        phone: cleanPhone,
      };
    }

    console.log('[twilio] No contact found for phone:', cleanPhone);
    return null;
  } catch (error) {
    console.error('[twilio] Caller lookup failed:', error);
    return null;
  }
}
